import { apiClient } from "./api-client";
import { urlBase64ToUint8Array } from "./url-base64";

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY;

/** 通知が利用可能か判定（VAPID 鍵未設定時も false） */
export const isPushSupported = (): boolean => {
  return (
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    !!VAPID_PUBLIC_KEY
  );
};

/** 通知権限をリクエスト */
export const requestNotificationPermission =
  async (): Promise<NotificationPermission> => {
    return Notification.requestPermission();
  };

/**
 * Push 通知の購読を開始しサーバーに登録する
 *
 * ローカル dev モード（vite devOptions.enabled=false）は SW が登録されないため
 * registration が null になり null を返す。トグルが即 OFF に戻るのは想定内。
 * SW が動く環境（vite preview / cloud 環境）でのみ実際に購読が成立する。
 */
export const subscribePush = async (): Promise<PushSubscription | null> => {
  if (!VAPID_PUBLIC_KEY) return null;

  // serviceWorker.ready は SW 未登録時に永遠にブロックするため getRegistration を使う
  const registration = await navigator.serviceWorker.getRegistration();
  if (!registration) return null;
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    // Safari/Firefox は string を受け付けないため Uint8Array に変換
    applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
  });

  await apiClient.post("/push/subscriptions/me", {
    endpoint: subscription.endpoint,
    keys: {
      p256dh: subscription.toJSON().keys?.p256dh ?? "",
      auth: subscription.toJSON().keys?.auth ?? "",
    },
  });

  return subscription;
};

/** Push 通知の購読を解除しサーバーから削除する */
export const unsubscribePush = async (): Promise<void> => {
  const registration = await navigator.serviceWorker.getRegistration();
  if (!registration) return;
  const subscription = await registration.pushManager.getSubscription();

  // ブラウザ側を先に解除する。サーバー削除が失敗しても次回 web-push 送信時に
  // 404/410 → sendOrCleanup が DB を掃除するため整合は最終的に取れる。
  if (subscription) await subscription.unsubscribe();
  await apiClient.delete("/push/subscriptions/me");
};

/** 現在の購読状態を取得する */
export const getPushSubscription =
  async (): Promise<PushSubscription | null> => {
    if (!isPushSupported()) return null;
    // ready は SW 未登録時に永遠に待つため getRegistration を使う
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) return null;
    return registration.pushManager.getSubscription();
  };
